import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getPatientById,
  getPatientHistory,
  getPatientAppointments
} from "@/data/mockData";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import {
  ChevronLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Droplets,
  AlertTriangle,
  Heart,
  Activity,
  Clock,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const PatientDetailPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("history");

  const patient = getPatientById(patientId);
  const history = getPatientHistory(patientId);
  const appointments = getPatientAppointments(patientId);

  if (!patient) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Paziente non trovato</p>
            <Button variant="link" onClick={() => navigate("/dashboard/patients")}>
              Torna alla lista
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Extract glycemia trend data
  const glycemiaTrend = history
    .flatMap(h => h.results.filter(r => r.name === "Glicemia").map(r => ({
      date: format(new Date(h.date), "MMM yy", { locale: it }),
      value: r.value
    })))
    .reverse();

  // Extract blood pressure trend
  const bpTrend = history
    .flatMap(h => {
      const systolic = h.results.find(r => r.name === "Pressione Sistolica");
      const diastolic = h.results.find(r => r.name === "Pressione Diastolica");
      if (systolic && diastolic) {
        return [{
          date: format(new Date(h.date), "MMM yy", { locale: it }),
          sistolica: systolic.value,
          diastolica: diastolic.value
        }];
      }
      return [];
    })
    .reverse();

  const getStatusIcon = (status) => {
    switch (status) {
      case "normal": return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "high": return <TrendingUp className="h-4 w-4 text-warning" />;
      case "low": return <TrendingDown className="h-4 w-4 text-info" />;
      default: return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "normal": 
        return <Badge className="bg-success/10 text-success border-success/20">Normale</Badge>;
      case "high": 
        return <Badge className="bg-warning/10 text-warning border-warning/20">Alto</Badge>;
      case "low": 
        return <Badge className="bg-info/10 text-info border-info/20">Basso</Badge>;
      default: 
        return <Badge variant="outline">N/A</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back button */}
      <Button
        variant="ghost"
        className="gap-2"
        onClick={() => navigate("/dashboard/patients")}
      >
        <ChevronLeft className="h-4 w-4" />
        Torna alla lista
      </Button>

      {/* Patient Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <Avatar className="h-20 w-20 border-4 border-primary/20">
              <AvatarFallback className="bg-primary/10 text-primary text-2xl font-semibold">
                {patient.name.split(" ").map(n => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="text-2xl font-bold">{patient.name}</h1>
                <p className="text-muted-foreground font-mono">{patient.fiscalCode}</p>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{patient.age} anni ({patient.dateOfBirth})</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{patient.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{patient.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{patient.address}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="gap-1">
                  <Droplets className="h-3 w-3" />
                  Gruppo: {patient.bloodType}
                </Badge>
                {patient.allergies.length > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Allergie: {patient.allergies.join(", ")}
                  </Badge>
                )}
                {patient.chronicConditions.map((condition, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    <Heart className="h-3 w-3" />
                    {condition}
                  </Badge>
                ))}
              </div>
            </div>

            <Button className="gap-2">
              <Activity className="h-4 w-4" />
              Nuova Visita
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="history" className="gap-2">
            <FileText className="h-4 w-4" />
            Storia Clinica
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Trend
          </TabsTrigger>
          <TabsTrigger value="appointments" className="gap-2">
            <Calendar className="h-4 w-4" />
            Appuntamenti
          </TabsTrigger>
        </TabsList>

        {/* Clinical History */}
        <TabsContent value="history" className="mt-6">
          <ScrollArea className="h-[500px]">
            <div className="space-y-4 pr-4">
              {history.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Nessun esame registrato</p>
                  </CardContent>
                </Card>
              ) : (
                history.map((exam, index) => (
                  <Card key={exam.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">
                            {format(new Date(exam.date), "d MMMM yyyy", { locale: it })}
                          </CardTitle>
                          <CardDescription>{exam.services.join(", ")}</CardDescription>
                        </div>
                        <Badge variant="outline">{exam.doctor}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Parametro</TableHead>
                            <TableHead className="text-right">Valore</TableHead>
                            <TableHead className="text-right">Rif.</TableHead>
                            <TableHead className="text-right">Stato</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {exam.results.map((result, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{result.name}</TableCell>
                              <TableCell className="text-right">
                                <span className={cn(
                                  "font-semibold",
                                  result.status === "normal" && "text-success",
                                  result.status === "high" && "text-warning",
                                  result.status === "low" && "text-info"
                                )}>
                                  {result.value} {result.unit}
                                </span>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground text-sm">
                                {result.range}
                              </TableCell>
                              <TableCell className="text-right">
                                {getStatusBadge(result.status)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {exam.notes && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">Note:</span> {exam.notes}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Glycemia Trend */}
            {glycemiaTrend.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Droplets className="h-5 w-5 text-info" />
                    Andamento Glicemia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={glycemiaTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[60, 150]} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }} 
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="hsl(var(--info))"
                          strokeWidth={2}
                          dot={{ fill: 'hsl(var(--info))' }}
                          name="Glicemia (mg/dL)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-success" />
                      <span className="text-muted-foreground">Normale: 70-100</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Blood Pressure Trend */}
            {bpTrend.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Heart className="h-5 w-5 text-destructive" />
                    Andamento Pressione
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={bpTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[50, 160]} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }} 
                        />
                        <Line
                          type="monotone"
                          dataKey="sistolica"
                          stroke="hsl(var(--destructive))"
                          strokeWidth={2}
                          dot={{ fill: 'hsl(var(--destructive))' }}
                          name="Sistolica (mmHg)"
                        />
                        <Line
                          type="monotone"
                          dataKey="diastolica"
                          stroke="hsl(var(--warning))"
                          strokeWidth={2}
                          dot={{ fill: 'hsl(var(--warning))' }}
                          name="Diastolica (mmHg)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Appointments */}
        <TabsContent value="appointments" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Storico Appuntamenti</CardTitle>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <div className="py-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nessun appuntamento</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt) => (
                    <div
                      key={apt.id}
                      className={cn(
                        "p-4 rounded-lg border flex items-center justify-between",
                        apt.status === "completed" && "bg-success/5 border-success/30",
                        apt.status === "scheduled" && "bg-muted/50"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(apt.date), "MMM", { locale: it })}
                          </p>
                          <p className="text-2xl font-bold">
                            {format(new Date(apt.date), "d")}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium">{apt.time}</p>
                          <p className="text-sm text-muted-foreground">
                            {apt.services.join(", ")}
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant={apt.status === "completed" ? "default" : "secondary"}
                        className={apt.status === "completed" ? "bg-success" : ""}
                      >
                        {apt.status === "completed" ? "Completato" : "Programmato"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PatientDetailPage;
